class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// Fixed one-of-a-kind quantity limiter for Shopify Dawn theme
(function() {
  'use strict';

  console.log('One-of-a-kind script loaded - FIXED VERSION');

  let isUpdating = false; // Prevent loops

  function limitQuantities() {
    console.log('Running limitQuantities...');
    
    if (isUpdating) {
      console.log('Currently updating, skipping...');
      return;
    }
    
    // Find one-of-a-kind items - use Set to avoid duplicates
    const oneOfAKindItems = document.querySelectorAll('.cart-item[data-one-of-a-kind="true"]');
    const processedVariants = new Set();
    
    if (oneOfAKindItems.length === 0) {
      console.log('No one-of-a-kind items found');
      return;
    }
    
    console.log(`Found ${oneOfAKindItems.length} one-of-a-kind elements (may include duplicates)`);
    
    oneOfAKindItems.forEach((item, index) => {
      const quantityInput = item.querySelector('input[name*="updates"], .quantity__input');
      
      if (quantityInput) {
        // Get variant ID from data attribute
        const variantId = quantityInput.getAttribute('data-quantity-variant-id');
        
        // Skip if we've already processed this variant
        if (processedVariants.has(variantId)) {
          console.log(`  Skipping duplicate variant ${variantId}`);
          return;
        }
        
        processedVariants.add(variantId);
        console.log(`Processing unique variant ${variantId}, current quantity: ${quantityInput.value}`);
        
        // Set max attribute
        quantityInput.setAttribute('max', '1');
        
        // If current value is > 1, fix it
        const currentQuantity = parseInt(quantityInput.value);
        if (currentQuantity > 1) {
          console.log(`  Fixing quantity from ${currentQuantity} to 1`);
          
          isUpdating = true;
          
          // Update Shopify cart
          if (variantId) {
            fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: variantId,
                quantity: 1
              })
            })
            .then(response => response.json())
            .then(data => {
              console.log('Cart updated successfully:', data);
              
              // Update ALL quantity inputs for this variant
              document.querySelectorAll(`input[data-quantity-variant-id="${variantId}"]`).forEach(input => {
                input.value = '1';
                input.setAttribute('data-cart-quantity', '1');
              });
              
              // Update totals on the page
              updatePageTotals(data);
              
              // Show message
              showMessage('One-of-a-kind item quantity limited to 1');
              
              isUpdating = false;
            })
            .catch(error => {
              console.error('Error updating cart:', error);
              isUpdating = false;
            });
          } else {
            console.log('  No variant ID found, just updating input');
            quantityInput.value = '1';
            isUpdating = false;
          }
        }
        
        // Add event listeners to prevent future changes
        if (!quantityInput.hasAttribute('data-limiter-attached')) {
          quantityInput.setAttribute('data-limiter-attached', 'true');
          
          quantityInput.addEventListener('input', function(e) {
            if (parseInt(this.value) > 1) {
              console.log('Preventing quantity > 1 on input');
              this.value = '1';
              showMessage('One-of-a-kind item quantity limited to 1');
            }
          });
          
          quantityInput.addEventListener('change', function(e) {
            const newValue = parseInt(this.value);
            if (newValue > 1) {
              console.log('Preventing quantity > 1 on change');
              this.value = '1';
              
              if (variantId && !isUpdating) {
                isUpdating = true;
                
                fetch('/cart/change.js', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    id: variantId,
                    quantity: 1
                  })
                })
                .then(response => response.json())
                .then(data => {
                  console.log('Cart updated on change:', data);
                  updatePageTotals(data);
                  showMessage('One-of-a-kind item quantity limited to 1');
                  isUpdating = false;
                })
                .catch(error => {
                  console.error('Error:', error);
                  isUpdating = false;
                });
              }
            }
          });
          
          console.log('  Event listeners attached');
        }
        
        // Disable plus buttons
        const plusButtons = item.querySelectorAll('.quantity__button[name="plus"], .quantity__button--plus, button[aria-label*="Increase"]');
        plusButtons.forEach(button => {
          if (!button.hasAttribute('data-limiter-disabled')) {
            button.setAttribute('data-limiter-disabled', 'true');
            
            // Replace the button to remove existing event listeners
            const newButton = button.cloneNode(true);
            button.parentNode.replaceChild(newButton, button);
            
            newButton.addEventListener('click', function(e) {
              e.preventDefault();
              e.stopPropagation();
              showMessage('One-of-a-kind item quantity limited to 1');
              return false;
            });
            
            console.log('  Plus button disabled');
          }
        });
        
      } else {
        console.log(`  No quantity input found for item ${index + 1}`);
      }
    });
  }
  
  // Update page totals after cart change
  function updatePageTotals(cart) {
    console.log('Updating page totals...', cart);
    
    // Update cart subtotal elements
    const subtotalSelectors = [
      '.cart-subtotal__price',
      '.totals__subtotal-value', 
      '.cart__subtotal',
      '[data-cart-subtotal]',
      '.subtotal',
      '.cart-subtotal .price',
      '.cart__footer .totals__subtotal .price',
      '.totals__total .price'
    ];
    
    subtotalSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        const formatted = formatMoney(cart.total_price);
        console.log(`Updating ${selector} to ${formatted}`);
        element.textContent = formatted;
      });
    });
    
    // Update individual item totals for the specific variant
    cart.items.forEach(item => {
      const itemElements = document.querySelectorAll(`[data-quantity-variant-id="${item.variant_id}"]`);
      itemElements.forEach(quantityInput => {
        const cartItemRow = quantityInput.closest('.cart-item');
        if (cartItemRow) {
          // Update line total price
          const priceElements = cartItemRow.querySelectorAll('.cart-item__price-wrapper .price, .cart-item__totals .price');
          priceElements.forEach(element => {
            const formatted = formatMoney(item.final_line_price);
            console.log(`Updating line item price to ${formatted}`);
            element.textContent = formatted;
          });
        }
      });
    });
    
    // Force refresh of any dynamic cart elements
    const cartRefreshEvent = new CustomEvent('cart:refresh', { detail: cart });
    document.dispatchEvent(cartRefreshEvent);
  }
  
  // Format money
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
  
  // Show message to user
  function showMessage(text) {
    console.log('Showing message:', text);
    
    // Remove existing notification
    const existingNotification = document.getElementById('one-of-a-kind-notification');
    if (existingNotification) {
      existingNotification.remove();
    }
    
    const notification = document.createElement('div');
    notification.id = 'one-of-a-kind-notification';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #dc3545;
      color: white;
      padding: 12px 16px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
      max-width: 300px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    `;
    notification.textContent = text;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 4000);
  }

  // Initialize
  function init() {
    console.log('Initializing one-of-a-kind limiter...');
    limitQuantities();
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Run when cart updates
  document.addEventListener('cart:updated', function() {
    setTimeout(limitQuantities, 500);
  });
  
  // Watch for DOM changes (cart drawer, etc.)
  const observer = new MutationObserver(function(mutations) {
    let shouldRun = false;
    
    mutations.forEach(function(mutation) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            if ((node.classList && node.classList.contains('cart-item')) ||
                (node.querySelector && node.querySelector('.cart-item'))) {
              shouldRun = true;
            }
          }
        });
      }
    });
    
    if (shouldRun && !isUpdating) {
      setTimeout(limitQuantities, 200);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

})();